/**
 * AdminDangerZone — host-only kill switches for the combo villa + cooking
 * amenity rollout. Lives at /admin/danger-zone. Reversible: a Restore button
 * flips both features back ON. Non-destructive — just toggles feature flags.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../config/api';
import { FiArrowLeft, FiAlertTriangle, FiCheck, FiRefreshCw, FiPower, FiCoffee, FiHome } from 'react-icons/fi';

const AdminDangerZone = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const flash = (text, type = 'success') => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3800);
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/danger-zone/status');
      setStatus(r.data);
    } catch (err) {
      flash(err.response?.data?.detail || 'Failed to load status', 'error');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    if (!u.is_admin) { navigate('/login'); return; }
    refresh();
  }, [navigate, refresh]);

  const run = async (endpoint, confirmMsg, successMsg) => {
    if (!window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      await api.post(endpoint);
      flash(successMsg);
      await refresh();
    } catch (err) {
      flash(err.response?.data?.detail || 'Action failed', 'error');
    }
    setBusy(false);
  };

  const killAll = () => run(
    '/admin/danger-zone/kill-combos-and-cooking',
    `KILL SWITCH #1 — Remove 3 new villas (7/8/9 BHK) AND the Cooking Space amenity?

This hides them site-wide (home cards, pricing table, booking flow, admin lists). Reversible via "Restore".

Continue?`,
    'All new features hidden. Site is back to the pre-Feb 2026 state.',
  );

  const killCombosOnly = () => run(
    '/admin/danger-zone/kill-combos-keep-cooking',
    `KILL SWITCH #2 — Remove ONLY the 3 new villas (7/8/9 BHK)?

The Cooking Space amenity stays live site-wide. Reversible via "Restore".

Continue?`,
    'Combo villas hidden. Cooking Space amenity still live.',
  );

  const restoreAll = () => run(
    '/admin/danger-zone/restore-combos-and-cooking',
    `Restore BOTH the 3 combo villas AND the Cooking Space amenity to the live site?`,
    'Restored. Combo villas + Cooking Space are now live.',
  );

  const comboOn = status?.combo_villas_enabled;
  const cookingOn = status?.cooking_amenity_enabled;

  return (
    <div className="min-h-screen bg-[#F7F8FA]" data-testid="admin-danger-zone">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <Link to="/admin" className="flex items-center gap-2 text-sm font-semibold text-[#1A1A2E] hover:text-[#F57C20]">
            <FiArrowLeft className="w-4 h-4" /> Back to Admin
          </Link>
          <button
            onClick={refresh}
            disabled={loading}
            data-testid="dz-refresh-btn"
            className="text-xs px-3 py-1.5 rounded-full bg-white border border-gray-200 text-[#1A1A2E] font-semibold hover:bg-gray-50 inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <FiRefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 mb-6">
          <div className="flex items-start gap-3 mb-2">
            <FiAlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
            <div>
              <h1 className="text-2xl font-bold text-red-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                Danger Zone — Combo Villas &amp; Cooking Amenity
              </h1>
              <p className="text-sm text-red-800/80 mt-1">
                Kill-switches for the Feb 2026 rollout. All actions are reversible — they toggle feature flags,
                they don&apos;t delete any data.
              </p>
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="dz-status-combos">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold uppercase tracking-wider text-[#6B7280]">Combo Villas (7/8/9 BHK)</p>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                comboOn ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'
              }`}>
                {comboOn ? 'LIVE' : 'HIDDEN'}
              </span>
            </div>
            <p className="text-sm text-[#1A1A2E]">
              Harmony (7BHK), Grandeur (8BHK), Paradise (9BHK) villa cards + pricing rows.
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="dz-status-cooking">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold uppercase tracking-wider text-[#6B7280]">Cooking Space Amenity</p>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                cookingOn ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'
              }`}>
                {cookingOn ? 'LIVE' : 'HIDDEN'}
              </span>
            </div>
            <p className="text-sm text-[#1A1A2E]">
              &quot;New Feature&quot; home-page strip + optional add-on at booking.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-4">
          {/* KILL SWITCH #1 */}
          <div className="bg-white border-2 border-red-300 rounded-xl p-6" data-testid="dz-kill-all-card">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <FiPower className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-[#1A1A2E] mb-1">Kill Switch #1 — Nuke Everything</h2>
                <p className="text-sm text-[#6B7280] mb-4">
                  Hides the 3 combo villas (7/8/9 BHK) <strong>and</strong> the Cooking Space amenity site-wide.
                  Use this if you want to revert to the pre-Feb 2026 state entirely.
                </p>
                <button
                  onClick={killAll}
                  disabled={busy || (!comboOn && !cookingOn)}
                  data-testid="dz-kill-all-btn"
                  className="px-5 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  <FiPower className="w-4 h-4" /> Nuke All (Combos + Cooking)
                </button>
              </div>
            </div>
          </div>

          {/* KILL SWITCH #2 */}
          <div className="bg-white border-2 border-amber-300 rounded-xl p-6" data-testid="dz-kill-combos-card">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <FiHome className="w-6 h-6 text-amber-700" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-[#1A1A2E] mb-1">Kill Switch #2 — Keep Cooking Only</h2>
                <p className="text-sm text-[#6B7280] mb-4">
                  Hides <strong>only</strong> the 3 combo villas. The Cooking Space amenity stays live.
                </p>
                <button
                  onClick={killCombosOnly}
                  disabled={busy || !comboOn}
                  data-testid="dz-kill-combos-btn"
                  className="px-5 py-2.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  <FiHome className="w-4 h-4" /> Hide Combos (Keep Cooking)
                </button>
              </div>
            </div>
          </div>

          {/* RESTORE */}
          <div className="bg-white border-2 border-emerald-300 rounded-xl p-6" data-testid="dz-restore-card">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <FiCheck className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-[#1A1A2E] mb-1">Restore Everything</h2>
                <p className="text-sm text-[#6B7280] mb-4">
                  Turns both features back ON. Use after either Kill Switch to roll forward again.
                </p>
                <button
                  onClick={restoreAll}
                  disabled={busy || (comboOn && cookingOn)}
                  data-testid="dz-restore-btn"
                  className="px-5 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  <FiCheck className="w-4 h-4" /> Restore Combos + Cooking
                </button>
              </div>
            </div>
          </div>
        </div>

        {status?.last_action && (
          <div className="mt-6 bg-white rounded-xl border border-gray-200 p-4" data-testid="dz-last-action">
            <p className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1">Last action</p>
            <p className="text-sm text-[#1A1A2E]">
              <span className="font-bold">{status.last_action.replace(/_/g, ' ')}</span>{' '}
              {status.last_at && <span className="text-[#6B7280]">· {new Date(status.last_at).toLocaleString('en-IN')}</span>}{' '}
              {status.last_by && <span className="text-[#6B7280]">· by {status.last_by}</span>}
            </p>
          </div>
        )}
      </div>

      {toast && (
        <div
          data-testid="dz-toast"
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 px-5 py-3 rounded-lg text-sm font-bold shadow-xl z-50 ${
            toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-emerald-600 text-white'
          }`}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
};

export default AdminDangerZone;
